"use client";

import type { UseChatHelpers } from "@ai-sdk/react";
import { motion } from "framer-motion";
import { memo } from "react";
import type { ChatMessage } from "@/lib/types";
import { Suggestion } from "./elements/suggestion";
import type { VisibilityType } from "./visibility-selector";
import equal from "fast-deep-equal"; 

type SuggestedActionsProps = {
  chatId: string;
  sendMessage: UseChatHelpers<ChatMessage>["sendMessage"];
  selectedVisibilityType: VisibilityType;
  customSuggestions?: string[];
};

function PureSuggestedActions({ 
  chatId, 
  sendMessage, 
  customSuggestions 
}: SuggestedActionsProps) {
  
  const initialActions = [
    "Predict flood risk for the Red River Basin at 6000m, grid H3",
    "Run analysis for Morris at 500m and dgrid S2",
    "Flood risk prediction for St-Jean Baptiste at 1000m (ISEA3H)",
    "Check flood risk in Emerson at 250m and grid h3"
  ];

  const displayActions = customSuggestions?.length ? customSuggestions : initialActions;

  return (
    <div className="grid w-full gap-2 grid-cols-1 2xl:grid-cols-2" data-testid="suggested-actions">
      {displayActions.map((suggestedAction, index) => (
        <motion.div
          key={`${chatId}-${suggestedAction}-${index}`} 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          transition={{ delay: 0.05 * index }}
        >
          <Suggestion
            onClick={(suggestion) => {
              sendMessage({ 
                role: "user", 
                parts: [{ type: "text", text: suggestion }] 
              });
            }}
            suggestion={suggestedAction}
          />
        </motion.div>
      ))}
    </div>
  );
}

export const SuggestedActions = memo(
  PureSuggestedActions,
  (prevProps, nextProps) => {
    if (prevProps.chatId !== nextProps.chatId) return false;
    if (prevProps.selectedVisibilityType !== nextProps.selectedVisibilityType) return false;
    if (!equal(prevProps.customSuggestions, nextProps.customSuggestions)) return false; 
    return true;
  }
);
